// src/components/uni/GenerativeDesignXAI.js

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import './GenerativeDesignXAI.css';
import Footer from '../components/Footer';
import { LanguageContext } from '../translations/LanguageContext';

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Helper to clamp the factor for Concept tab
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function GenerativeDesignXAI() {
  // Get language context
  const { language, translations } = useContext(LanguageContext);
  const t = translations[language].generativeDesignXAIPage;
  
  // -----------------------
  // State and Refs
  // -----------------------
  // Tabs
  const [activeTab, setActiveTab] = useState('summary');

  // Concept sliders
  const [tempFactor, setTempFactor] = useState(1);
  const [humidityFactor, setHumidityFactor] = useState(1);
  const [windFactor, setWindFactor] = useState(1);

  // Generator
  const [selectedCity, setSelectedCity] = useState('Munich');
  const [selectedMode, setSelectedMode] = useState('2D');
  const [inspectorMode, setInspectorMode] = useState(false);

  // Summary scene refs
  const summarySceneRef = useRef(null);
  const summaryRendererRef = useRef(null);
  const summarySceneObjRef = useRef(null);
  const summaryCameraRef = useRef(null);
  const summaryAnimationIdRef = useRef(null);

  // Concept scene refs
  const conceptSceneRef = useRef(null);
  const conceptRendererRef = useRef(null);
  const conceptSceneObjRef = useRef(null);
  const conceptCameraRef = useRef(null);
  const conceptAnimationIdRef = useRef(null);
  const conceptModelRef = useRef(null);

  // Generator scene refs
  const threeContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const animationRef = useRef(null);
  const controlsRef = useRef(null);

  // 3D Carousel track ref
  const carousel3DTrackRef = useRef(null);

  // -----------------------
  // Static Data
  // -----------------------
  // 2D images for the generator
  const twoDImages = {
    Amsterdam: '/GenerativeDesignXAI/2D/2D_Amsterdam.png',
    Barcelona: '/GenerativeDesignXAI/2D/2D_Barcelona.png',
    Berlin: '/GenerativeDesignXAI/2D/2D_Berlin.png',
    Cairo: '/GenerativeDesignXAI/2D/2D_Cairo.png',
    Dubai: '/GenerativeDesignXAI/2D/2D_Dubai.png',
    Riyadh: '/GenerativeDesignXAI/2D/2D_Riyadh.png',
    Geneva: '/GenerativeDesignXAI/2D/2D_Geneva.png',
    Stockholm: '/GenerativeDesignXAI/2D/2D_Stockholm.png',
    Munich: '/GenerativeDesignXAI/2D/2D_Munich.png'
  };

  // 3D models for generator & carousel
  const threeDModels = useMemo(
    () => ({
      Amsterdam: '/GenerativeDesignXAI/3D/3D_Amsterdam.glb',
      Berlin: '/GenerativeDesignXAI/3D/3D_Berlin.glb',
      Cairo: '/GenerativeDesignXAI/3D/3D_Cairo.glb',
      Dubai: '/GenerativeDesignXAI/3D/3D_Dubai.glb',
      Riyadh: '/GenerativeDesignXAI/3D/3D_Riyadh.glb',
      Geneva: '/GenerativeDesignXAI/3D/3D_Geneva.glb',
      Stockholm: '/GenerativeDesignXAI/3D/3D_Stockholm.glb',
      Munich: '/GenerativeDesignXAI/3D/3D_Munich.glb',
      Barcelona: '/GenerativeDesignXAI/3D/3D_Barcelona.glb'
    }),
    []
  );

  // For the "More than 100 Cities" 2D carousel
  const carouselCities = [
    { img: 'card_amsterdam.png', city: 'Amsterdam', country: 'Netherlands' },
    { img: 'card_barcelona.png', city: 'Barcelona', country: 'Spain' },
    { img: 'card_berlin.png', city: 'Berlin', country: 'Germany' },
    { img: 'card_dubai.png', city: 'Dubai', country: 'UAE' },
    { img: 'card_cairo.png', city: 'Cairo', country: 'Egypt' },
    { img: 'card_geneva.png', city: 'Geneva', country: 'Switzerland' },
    { img: 'card_laPaz.png', city: 'La Paz', country: 'Bolivia' },
    { img: 'card_munich.png', city: 'Munich', country: 'Germany' },
    { img: 'card_riyadh.png', city: 'Riyadh', country: 'Saudi Arabia' },
    { img: 'card_stockholm.png', city: 'Stockholm', country: 'Sweden' },
    { img: 'card_uzhhorod.png', city: 'Uzhhorod', country: 'Ukraine' }
  ];

  // -----------------------
  // SUMMARY SCENE
  // -----------------------
  useEffect(() => {
    // If we're not on the Summary tab, clean up.
    if (activeTab !== 'summary') {
      if (summaryAnimationIdRef.current) cancelAnimationFrame(summaryAnimationIdRef.current);
      if (summaryRendererRef.current) {
        summaryRendererRef.current.dispose();
        summaryRendererRef.current.domElement.remove();
      }
      if (summarySceneObjRef.current) {
        summarySceneObjRef.current.clear();
      }
      return;
    }

    // If no DOM ref, do nothing
    if (!summarySceneRef.current) return;

    const container = summarySceneRef.current;
    container.innerHTML = '';

    const width = container.clientWidth || 400;
    const height = container.clientHeight || 300;

    const scene = new THREE.Scene();
    summarySceneObjRef.current = scene;

    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 150, 300);
    camera.lookAt(0, 0, 0);
    summaryCameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    summaryRendererRef.current = renderer;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xcccccc, 0.8);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(100, 100, 100);
    scene.add(directionalLight);

    // Plane => scale(3,3,3)
    const textureLoader = new THREE.TextureLoader();
    const planeTexture = textureLoader.load('/GenerativeDesignXAI/2D/2D_Geneva.png');
    const planeGeom = new THREE.PlaneGeometry(200, 100);
    const planeMat = new THREE.MeshBasicMaterial({ map: planeTexture, side: THREE.DoubleSide });
    const plane = new THREE.Mesh(planeGeom, planeMat);
    plane.rotation.x = -Math.PI / 3;
    plane.position.y = -10;
    plane.scale.set(3.5, 3.5, 3.5);
    scene.add(plane);

    // Model => scale(0.35,0.35,0.35)
    const loader = new GLTFLoader();
    loader.load(
      '/GenerativeDesignXAI/3D/3D_Geneva.glb',
      (gltf) => {
        const model = gltf.scene;
        model.rotation.x = 0.25;
        model.scale.set(0.4, 0.4, 0.4);
        model.position.set(0, 90, 0);
        scene.add(model);

        function animate() {
          summaryAnimationIdRef.current = requestAnimationFrame(animate);
          model.rotation.y += 0.005;
          renderer.render(scene, camera);
        }
        animate();
      },
      undefined,
      (err) => console.error('Error loading summary model:', err)
    );

    const handleResize = () => {
      if (!container) return;
      const newW = container.clientWidth;
      const newH = container.clientHeight;
      renderer.setSize(newW, newH);
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (summaryAnimationIdRef.current) cancelAnimationFrame(summaryAnimationIdRef.current);
      renderer.dispose();
      scene.clear();
    };
  }, [activeTab]);

  // -----------------------
  // CONCEPT SCENE
  // -----------------------
  useEffect(() => {
    if (activeTab !== 'concept') {
      if (conceptAnimationIdRef.current) cancelAnimationFrame(conceptAnimationIdRef.current);
      if (conceptRendererRef.current) {
        conceptRendererRef.current.dispose();
        conceptRendererRef.current.domElement.remove();
      }
      if (conceptSceneObjRef.current) {
        conceptSceneObjRef.current.clear();
      }
      return;
    }

    if (!conceptSceneRef.current) return;
    const container = conceptSceneRef.current;
    container.innerHTML = '';

    const width = container.clientWidth || 400;
    const height = container.clientHeight || 300;

    const scene = new THREE.Scene();
    conceptSceneObjRef.current = scene;

    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 150, 300);
    camera.lookAt(0, 0, 0);
    conceptCameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    conceptRendererRef.current = renderer;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xcccccc, 0.8);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(100, 100, 100);
    scene.add(directionalLight);

    // Plane => scale(2,2,2)
    const textureLoader = new THREE.TextureLoader();
    const planeTexture = textureLoader.load('/GenerativeDesignXAI/2D/2D_Geneva.png');
    const planeGeom = new THREE.PlaneGeometry(200, 100);
    const planeMat = new THREE.MeshBasicMaterial({ map: planeTexture, side: THREE.DoubleSide });
    const plane = new THREE.Mesh(planeGeom, planeMat);
    plane.rotation.x = -Math.PI / 3;
    plane.position.y = -10;
    plane.scale.set(2, 2, 2);
    scene.add(plane);

    // Model => scale(0.25,0.25,0.25)
    const loader = new GLTFLoader();
    loader.load(
      '/GenerativeDesignXAI/3D/3D_Geneva.glb',
      (gltf) => {
        const model = gltf.scene;
        model.rotation.x = 0.25;
        model.scale.set(0.25, 0.25, 0.25);
        model.position.set(0, 90, 0);
        scene.add(model);

        conceptModelRef.current = model;

        // Precompute userData
        model.traverse((child) => {
          if (child.isMesh && child.geometry && child.material) {
            child.geometry.computeBoundingBox();
            child.userData.originalColor = child.material.color.clone();
            child.userData.originalScale = child.scale.clone();
            child.userData.originalPosition = child.position.clone();
            child.userData.bbMinY = child.geometry.boundingBox?.min.y || 0;
          }
        });

        const animateConcept = () => {
          conceptAnimationIdRef.current = requestAnimationFrame(animateConcept);
          model.rotation.y += 0.005;
          renderer.render(scene, camera);
        };
        animateConcept();
      },
      undefined,
      (err) => console.error('Error loading concept model:', err)
    );

    const handleResize = () => {
      const newW = container.clientWidth;
      const newH = container.clientHeight;
      renderer.setSize(newW, newH);
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (conceptAnimationIdRef.current) cancelAnimationFrame(conceptAnimationIdRef.current);
      renderer.dispose();
      scene.clear();
    };
  }, [activeTab]);

  // Concept slider effect => clamp up to 3
  useEffect(() => {
    if (activeTab !== 'concept') return;
    const model = conceptModelRef.current;
    if (!model) return;

    model.traverse((child) => {
      if (child.isMesh && child.userData.originalColor && child.userData.originalScale) {
        const { r, g, b } = child.userData.originalColor;
        let rawFactor = 1.0;

        const threshold = 0.02;
        const rgDiff = Math.abs(r - g);
        const rbDiff = Math.abs(r - b);
        const gbDiff = Math.abs(g - b);

        // Decide which factor to apply based on dominant channel
        if (rgDiff < threshold && rbDiff < threshold && gbDiff < threshold) {
          // White => wind
          rawFactor = windFactor;
        } else if (r >= g && r >= b) {
          // Red => temp
          rawFactor = tempFactor;
        } else if (b >= r && b >= g) {
          // Blue => humidity
          rawFactor = humidityFactor;
        } else {
          // fallback => wind
          rawFactor = windFactor;
        }

        const scaleFactor = clamp(rawFactor, 0.2, 3);

        // color adjustments
        let finalR = r * scaleFactor;
        let finalG = g * scaleFactor;
        let finalB = b * scaleFactor;

        if (rgDiff < threshold && rbDiff < threshold && gbDiff < threshold && scaleFactor === 3) {
          // If it was white originally, keep white if at max factor
          finalR = 1;
          finalG = 1;
          finalB = 1;
        } else {
          finalR = clamp(finalR, 0, 1);
          finalG = clamp(finalG, 0, 1);
          finalB = clamp(finalB, 0, 1);
        }
        child.material.color.setRGB(finalR, finalG, finalB);

        // scale Y
        const origScale = child.userData.originalScale;
        child.scale.set(origScale.x, origScale.y * scaleFactor, origScale.z);

        // reposition
        const bottomOffset = child.userData.bbMinY || 0;
        const origPos = child.userData.originalPosition;
        const oldY = origScale.y;
        const newY = origScale.y * scaleFactor;
        const deltaY = newY - oldY;

        child.position.set(origPos.x, origPos.y - bottomOffset * deltaY, origPos.z);
      }
    });
  }, [activeTab, tempFactor, humidityFactor, windFactor]);

  // -----------------------
  // GENERATOR SCENE (2D/3D)
  // -----------------------
  useEffect(() => {
    // If we switch away from 3D mode or there's no container, clean up 3D
    if (selectedMode !== '3D' || !threeContainerRef.current) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current.domElement?.remove();
      }
      if (sceneRef.current) {
        sceneRef.current.clear();
      }
      return;
    }

    // Now set up 3D if needed
    const container = threeContainerRef.current;
    container.innerHTML = '';
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(400, 400, 400);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    const ambientLight = new THREE.AmbientLight(0xcccccc, 0.8);
    scene.add(ambientLight);

    // Load chosen city
    const loader = new GLTFLoader();
    loader.load(
      threeDModels[selectedCity],
      (gltf) => {
        const model = gltf.scene;
        model.scale.set(0.5, 0.5, 0.5);
        scene.add(model);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.autoRotate = !inspectorMode;
        controls.autoRotateSpeed = 1.0;
        controlsRef.current = controls;

        function animate() {
          animationRef.current = requestAnimationFrame(animate);
          controls.update();
          // Extra rotation if not in inspector mode
          if (!inspectorMode && model) {
            model.rotation.y += 0.01;
          }
          renderer.render(scene, camera);
        }
        animate();
      },
      undefined,
      (err) => console.error('Error loading generator model:', err)
    );

    const handleResize = () => {
      const newW = container.clientWidth;
      const newH = container.clientHeight;
      renderer.setSize(newW, newH);
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    const handleKeyDown = (e) => {
      if ((e.key === 'q' || e.key === 'Q') && inspectorMode) {
        setInspectorMode(false);
        if (controlsRef.current) {
          controlsRef.current.autoRotate = true;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (controlsRef.current) controlsRef.current.dispose();
      renderer.dispose();
      scene.clear();
    };
  }, [selectedMode, selectedCity, threeDModels, inspectorMode]);

  // ------------------------------------------------
  // 3D CAROUSEL (Load models in a single “infinite” row)
  // ------------------------------------------------
  useEffect(() => {
    // The cities we want in the 3D carousel:
    const baseKeys = ['Amsterdam', 'Berlin', 'Cairo', 'Geneva', 'Stockholm'];
    const cityKeys = [...baseKeys, ...baseKeys]; // duplicate for infinite loop

    // If we have no track ref, do nothing
    if (!carousel3DTrackRef.current) return;

    const track = carousel3DTrackRef.current;
    track.innerHTML = '';

    const loader = new GLTFLoader();
    const miniRenderers = [];
    const miniAnimationIds = [];
    const LOAD_DELAY_MS = 250; // mild stagger to avoid GPU overload

    cityKeys.forEach((city, i) => {
      setTimeout(() => {
        // skip if same city was just appended
        const lastItem = track.lastElementChild;
        const lastCity = lastItem
          ?.querySelector('.gdxai-carousel-city-name')
          ?.textContent;
        if (lastCity === city) return;

        // Create the item container
        const itemDiv = document.createElement('div');
        itemDiv.className = 'gdxai-carousel-item';

        // Container for mini scene
        const containerDiv = document.createElement('div');
        containerDiv.className = 'gdxai-three-container';
        containerDiv.style.width = '400px';
        containerDiv.style.height = '300px';
        itemDiv.appendChild(containerDiv);

        // Label
        const label = document.createElement('p');
        label.textContent = city;
        label.className = 'gdxai-carousel-city-name';
        itemDiv.appendChild(label);

        // Add item to track
        track.appendChild(itemDiv);

        // Build mini scene + renderer
        const miniScene = new THREE.Scene();
        miniScene.background = new THREE.Color(0xffffff);

        const miniCamera = new THREE.PerspectiveCamera(45, 400 / 300, 0.1, 1000);
        miniCamera.position.set(250, 250, 250);
        miniCamera.lookAt(0, 0, 0);

        const miniRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        miniRenderer.setSize(400, 300);
        containerDiv.appendChild(miniRenderer.domElement);

        miniRenderers.push(miniRenderer);

        // Lights
        const dLight = new THREE.DirectionalLight(0xffffff, 1);
        dLight.position.set(5, 5, 5);
        miniScene.add(dLight);

        const aLight = new THREE.AmbientLight(0xcccccc, 0.8);
        miniScene.add(aLight);

        // Load the model
        loader.load(
          threeDModels[city],
          (gltf) => {
            const miniModel = gltf.scene;
            miniModel.scale.set(0.3, 0.3, 0.3);
            miniScene.add(miniModel);

            // Animation
            let animId = 0;
            const animateMini = () => {
              animId = requestAnimationFrame(animateMini);
              miniModel.rotation.y += 0.01;
              miniRenderer.render(miniScene, miniCamera);
            };
            animateMini();

            miniAnimationIds.push(() => cancelAnimationFrame(animId));
          },
          undefined,
          (err) => console.error(`Error loading 3D model for ${city}:`, err)
        );
      }, i * LOAD_DELAY_MS);
    });

    // Cleanup
    return () => {
      // Cancel all animation frames
      miniAnimationIds.forEach((cancelFn) => cancelFn && cancelFn());
      // Dispose all mini renderers
      miniRenderers.forEach((r) => {
        r.dispose();
        if (r.domElement) {
          r.domElement.remove();
        }
      });
      track.innerHTML = '';
    };
  }, [threeDModels]);

  // -----------------------
  // Dynamic Tab Content
  // -----------------------
  const tabContent = {
    summary: (
      <div className="gdxai-summary-row">
        <div className="gdxai-summary-left">
          {t.summaryContent.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        <div className="gdxai-summary-right">
          <div ref={summarySceneRef} className="gdxai-three-container" />
        </div>
      </div>
    ),

    concept: (
      <div className="gdxai-summary-row">
        <div className="gdxai-summary-left">
          <p>
            {t.conceptContent.parametersIntro}
          </p>
          <ul>
            {t.conceptContent.parameters.map((param, index) => (
              <li key={index}><strong>{param.split(':')[0]}:</strong> {param.split(':')[1]}</li>
            ))}
          </ul>
          <h3>{t.conceptContent.innovationsTitle}</h3>
          <ul>
            {t.conceptContent.innovations.map((innovation, index) => (
              <li key={index}>{innovation}</li>
            ))}
          </ul>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label>
              {t.conceptContent.sliderLabels.temperature}
              <input
                type="range"
                min="0"
                max="2"
                step="0.01"
                value={tempFactor}
                onChange={(e) => setTempFactor(Number(e.target.value))}
              />
            </label>
            <label>
              {t.conceptContent.sliderLabels.humidity}
              <input
                type="range"
                min="0"
                max="2"
                step="0.01"
                value={humidityFactor}
                onChange={(e) => setHumidityFactor(Number(e.target.value))}
              />
            </label>
            <label>
              {t.conceptContent.sliderLabels.windSpeed}
              <input
                type="range"
                min="0"
                max="2"
                step="0.01"
                value={windFactor}
                onChange={(e) => setWindFactor(Number(e.target.value))}
              />
            </label>
          </div>
        </div>
        <div className="gdxai-summary-right">
          <div ref={conceptSceneRef} className="gdxai-three-container" />
        </div>
      </div>
    ),

    design: (
      <>
        <h3>{t.designContent.processTitle}</h3>
        <ol>
          {t.designContent.processList.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ol>
        <h3>{t.designContent.learningsTitle}</h3>
        <ul>
          {t.designContent.learningsList.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </>
    )
  };

  return (
    <div className="generative-design-x-ai-page">
      {/* Hero Section */}
      <section className="gdxai-hero">
        <div className="gdxai-hero-left">
          <h1 className="gdxai-hero-title">{t.heroTitle}</h1>
          <p className="gdxai-hero-subtitle">
            {t.heroSubtitle}
            <br />
            <span className="gdxai-hero-coded">{t.heroCodedIn}</span>
          </p>
        </div>
        <div className="gdxai-hero-right">
          <img
            src="/GenerativeDesignXAI/Hero.gif"
            alt="Hero"
            className="gdxai-hero-image"
          />
        </div>
      </section>

      {/* Tabs */}
      <nav className="gdxai-tabs">
        <button
          className={`gdxai-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          {t.summaryTitle}
        </button>
        <button
          className={`gdxai-tab ${activeTab === 'concept' ? 'active' : ''}`}
          onClick={() => setActiveTab('concept')}
        >
          {t.conceptTitle}
        </button>
        <button
          className={`gdxai-tab ${activeTab === 'design' ? 'active' : ''}`}
          onClick={() => setActiveTab('design')}
        >
          {t.designTitle}
        </button>
      </nav>

      {/* Dynamic Tab Content */}
      <section className="gdxai-summary-text">
        {tabContent[activeTab]}
      </section>

      {/* Fingerprint Steps */}
      <section className="gdxai-fingerprint-steps">
        <h2>{t.fingerprintStepsTitle}</h2>
        <div className="gdxai-steps-container">
          <div className="gdxai-step">
            <img
              src="/GenerativeDesignXAI/FingerprintStep1.png"
              alt="Fingerprint Step 1"
              className="gdxai-step-image"
            />
            <h3>{t.steps.fingerprint.step1Title}</h3>
            <ul>
              {t.steps.fingerprint.step1List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-step">
            <img
              src="/GenerativeDesignXAI/FingerprintStep2.png"
              alt="Fingerprint Step 2"
              className="gdxai-step-image"
            />
            <h3>{t.steps.fingerprint.step2Title}</h3>
            <ul>
              {t.steps.fingerprint.step2List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-step">
            <img
              src="/GenerativeDesignXAI/FingerprintStep3.png"
              alt="Fingerprint Step 3"
              className="gdxai-step-image"
            />
            <h3>{t.steps.fingerprint.step3Title}</h3>
            <ul>
              {t.steps.fingerprint.step3List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-step">
            <img
              src="/GenerativeDesignXAI/FingerprintStep4.png"
              alt="Fingerprint Step 4"
              className="gdxai-step-image"
            />
            <h3>{t.steps.fingerprint.step4Title}</h3>
            <ul>
              {t.steps.fingerprint.step4List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* More than 100 Cities (2D Carousel) */}
      <section className="gdxai-more-cities">
        <h2>{t.moreThanCities}</h2>
        <div className="gdxai-carousel">
          <div className="gdxai-carousel-track">
            {carouselCities.concat(carouselCities).map((entry, idx) => (
              <div className="gdxai-carousel-item" key={idx}>
                <img
                  src={`/GenerativeDesignXAI/${entry.img}`}
                  alt={entry.city}
                />
                <p className="gdxai-carousel-city-name">
                  {entry.city}, {entry.country}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEGGO Brick Steps */}
      <section className="gdxai-leggobrick-steps">
        <h2>{t.leggobrickStepsTitle}</h2>
        <div className="gdxai-leggobrick-steps-container">
          <div className="gdxai-leggobrick-step">
            <img
              src="/GenerativeDesignXAI/FingerbrickStep1.png"
              alt="LEGGO Brick Step 1"
              className="gdxai-leggobrick-step-image"
            />
            <h3>{t.steps.leggobrick.step1Title}</h3>
            <ul>
              {t.steps.leggobrick.step1List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-leggobrick-step">
            <img
              src="/GenerativeDesignXAI/FingerbrickStep2.png"
              alt="LEGGO Brick Step 2"
              className="gdxai-leggobrick-step-image"
            />
            <h3>{t.steps.leggobrick.step2Title}</h3>
            <ul>
              {t.steps.leggobrick.step2List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-leggobrick-step">
            <img
              src="/GenerativeDesignXAI/FingerbrickStep3.png"
              alt="LEGGO Brick Step 3"
              className="gdxai-leggobrick-step-image"
            />
            <h3>{t.steps.leggobrick.step3Title}</h3>
            <ul>
              {t.steps.leggobrick.step3List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-leggobrick-step">
            <img
              src="/GenerativeDesignXAI/FingerbrickStep4.png"
              alt="LEGGO Brick Step 4"
              className="gdxai-leggobrick-step-image"
            />
            <h3>{t.steps.leggobrick.step4Title}</h3>
            <ul>
              {t.steps.leggobrick.step4List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="gdxai-leggobrick-step">
            <img
              src="/GenerativeDesignXAI/FingerbrickStep5.png"
              alt="LEGGO Brick Step 5"
              className="gdxai-leggobrick-step-image"
            />
            <h3>{t.steps.leggobrick.step5Title}</h3>
            <ul>
              {t.steps.leggobrick.step5List.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 3D Modelle Karussell */}
      <section className="gdxai-more-cities">
        <h2>{t.modelCarouselTitle}</h2>
        <div className="gdxai-carousel">
          <div className="gdxai-carousel-track" ref={carousel3DTrackRef}></div>
        </div>
      </section>

      {/* 2D & 3D Models Generator */}
      <section className="gdxai-generator-section">
        <h2>{t.generatorTitle}</h2>
        <div className="gdxai-generator-content">
          <div
            className="gdxai-generator-display"
            onClick={selectedMode === '3D' ? () => setInspectorMode(true) : undefined}
            title={
              selectedMode === '3D'
                ? inspectorMode
                  ? 'Inspector Mode ON'
                  : 'Inspector Mode OFF'
                : ''
            }
          >
            {selectedMode === '2D' ? (
              <img
                src={twoDImages[selectedCity]}
                alt={`${selectedCity} 2D visualization`}
                className="gdxai-preview-2d"
              />
            ) : (
              <div ref={threeContainerRef} className="gdxai-three-container" />
            )}
            {selectedMode === '3D' && (
              <div className={`inspector-overlay ${inspectorMode ? 'active' : ''}`}>
                <span>{inspectorMode ? t.inspectorModeOn : t.inspectorModeOff}</span>
              </div>
            )}
          </div>

          <div className="gdxai-generator-controls">
            <select
              id="citySelect"
              name="citySelect"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="gdxai-select"
            >
              <option value="Amsterdam">Amsterdam</option>
              <option value="Barcelona">Barcelona</option>
              <option value="Berlin">Berlin</option>
              <option value="Cairo">Cairo</option>
              <option value="Dubai">Dubai</option>
              <option value="Riyadh">Riyadh</option>
              <option value="Geneva">Geneva</option>
              <option value="Stockholm">Stockholm</option>
              <option value="Munich">Munich</option>
            </select>

            <select
              id="modeSelect"
              name="modeSelect"
              value={selectedMode}
              onChange={(e) => {
                setSelectedMode(e.target.value);
                setInspectorMode(false);
                if (controlsRef.current) {
                  controlsRef.current.autoRotate = e.target.value !== '3D';
                }
              }}
              className="gdxai-select"
            >
              <option value="2D">2D</option>
              <option value="3D">3D</option>
            </select>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default GenerativeDesignXAI;
